import { IconExclamation } from '@components/icons/exclamation';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconPersonOff } from '@components/icons/person-off';
import { IconSkip } from '@components/icons/skip';
import { IconTrigger } from '@components/icons/trigger';
import { IconTriggerOff } from '@components/icons/trigger-off';
import { StatusIndicator, StatusIndicatorProps } from '@components/status-indicator';
import { getScopedTranslate } from '@locale/translate';

import { BASE_ACTION_TYPE } from 'api/types';
import { User } from 'api/users/requests';
import { NodeMenu } from 'ui/actions/common';
import { Assignees } from 'ui/journey-builder/node-types/common';

import { TravellerJourneyNode } from './types';

type Action = {
  isCompleted?: boolean;
  isInProgress?: boolean;
  isSkipped?: boolean;
  isHalted?: boolean;
  isError?: boolean;
  isAutomatic?: boolean;
  assignees?: User[];
};

export const NodeExecutionMethod = ({
  action,
  overwriteError,
  overwriteVariant,
  overwriteRatio,
}: {
  action: Action;
  overwriteError?: boolean;
  overwriteVariant?: string;
  overwriteRatio?: number;
}) => {
  const ratio = overwriteRatio ?? (action.isCompleted ? 1 : action.isInProgress ? 0.5 : 0);
  const variant =
    overwriteVariant ??
    (action.isCompleted
      ? 'completed'
      : action.isInProgress
      ? 'inProgress'
      : action.isSkipped
      ? 'skipped'
      : 'notStarted');

  const isHalted = action.isHalted && !action.isCompleted;

  if (!action.isSkipped && (action.isError || overwriteError)) {
    return <StatusIndicator variant="error" icon={<IconExclamation />} ratio={ratio} />;
  }
  const indicatorProps = {
    variant,
    ratio,
  } as StatusIndicatorProps;
  if (action.isSkipped) {
    return <StatusIndicator variant="skipped" ratio={0} icon={<IconSkip />} />;
  }
  if (action.isAutomatic) {
    return (
      <StatusIndicator {...indicatorProps} icon={isHalted ? <IconTriggerOff /> : <IconTrigger />} />
    );
  }

  return (
    <Assignees
      assignees={action.assignees}
      {...indicatorProps}
      icon={isHalted ? <IconPersonOff /> : undefined}
    />
  );
};

export const NodeActions = ({
  node,
  overwriteError,
}: {
  node: TravellerJourneyNode;
  overwriteError?: boolean;
}) => {
  return (
    <>
      {!node.data?.isSkipped && !node.data?.isConfigured && <IconFormIndicatorWarning />}
      <NodeExecutionMethod action={node.data} overwriteError={overwriteError} />
      <NodeMenu
        actionId={node.id}
        actionType={node.type}
        hasFlowReached={node.data.hasFlowReached}
        isAutomatic={node.data.isAutomatic}
        isSkipped={node.data.isSkipped}
        isCompleted={node.data.isCompleted}
        travellerJourneyId={node.data.travellerJourneyId}
      />
    </>
  );
};

const errorLocale = {
  en: {
    deliveryFailure: 'Delivery failure',
    creationFailure: 'Creation failure',
    conversionFailure: 'Conversion failure',
    mailDeliveryFailure: 'Mail delivery failure',
    smsDeliveryFailure: 'SMS delivery failure',
    journeyFailedToStart: 'Journey failed to start',
    runFailure: 'Run failure',
  },
};

const { t: errorTranslate } = getScopedTranslate(errorLocale);

export const actionErrorTitle: Record<BASE_ACTION_TYPE, string> = {
  anonymize_text: errorTranslate('runFailure'),
  break_loop: errorTranslate('runFailure'),
  combo_send_form: errorTranslate('deliveryFailure'),
  calendar_event: errorTranslate('creationFailure'),
  code: errorTranslate('runFailure'),
  create_document: errorTranslate('creationFailure'),
  convert_document: errorTranslate('conversionFailure'),
  send_email: errorTranslate('mailDeliveryFailure'),
  send_sms: errorTranslate('smsDeliveryFailure'),
  journey_start: errorTranslate('journeyFailedToStart'),
  journey_triggers: errorTranslate('journeyFailedToStart'),
  journey_branch: errorTranslate('runFailure'),
  journey_merge: errorTranslate('runFailure'),
  journey_loop: errorTranslate('runFailure'),
  wait_for_time: errorTranslate('runFailure'),
  wait_until_fixed_date: errorTranslate('runFailure'),
  wait_until_relative_date: errorTranslate('runFailure'),
  placeholder: errorTranslate('runFailure'),
  integration: errorTranslate('creationFailure'),
  webhook: errorTranslate('runFailure'),
  condition: errorTranslate('runFailure'),
  condition_v2: errorTranslate('runFailure'),
  formatter: errorTranslate('runFailure'),
  ask_ai: errorTranslate('runFailure'),
  ask_ai_agent: errorTranslate('runFailure'),
  update_fields: errorTranslate('runFailure'),
  add_to_journey: errorTranslate('runFailure'),
  update_traveller: errorTranslate('runFailure'),
  ghost: errorTranslate('runFailure'),
  branch_base: errorTranslate('runFailure'),
  hidden: errorTranslate('runFailure'),
  extract_text: errorTranslate('runFailure'),
};
